import { ChevronDownIcon, PlusIcon } from "@heroicons/react/24/outline";
import React from "react";

/**
 * Shared Figma building blocks for the Account settings → Profile page.
 * Colours / radii are taken from the Figma frame:
 *   input fill #EBEBEC, divider #F1F1F3, primary #0485F7, radius xl (12px) / pill / 3xl (24px) cards
 */
const INPUT_BASE =
    "w-full rounded-xl border border-transparent bg-[#EBEBEC] px-3 text-sm font-normal text-gray-900 " +
    "placeholder:text-gray-500 outline-none transition-colors focus:border-blue-500 focus:bg-white " +
    "disabled:cursor-not-allowed disabled:opacity-60";

/**
 * login.tsx saves i18nextLng with JSON.stringify, so i18n.language can be `"en"` (with quotes).
 * Intl rejects that as a language tag, so the quotes are removed before formatting dates.
 */
export const getDateLanguage = (language: string): string => {
    return language.replace(/"/g, "") || "en";
};

export const isHttpUrl = (value: string): boolean => {
    try {
        const url = new URL(value);
        return url.protocol === "http:" || url.protocol === "https:";
    } catch {
        return false;
    }
};

// ── Labels / errors ──────────────────────────────────────────────────────
export const FieldLabel: React.FC<{
    children: React.ReactNode;
    required?: boolean;
    htmlFor?: string;
}> = ({ children, required, htmlFor }) => (
    <label htmlFor={htmlFor} className="inline-flex items-center gap-0.5 text-sm font-normal leading-5 text-gray-500">
        {children}
        {required && <span className="text-red-500">*</span>}
    </label>
);

export const FieldError: React.FC<{ message?: string }> = ({ message }) => {
    if (!message) return null;
    return <p className="mt-0.5 text-xs text-red-500">{message}</p>;
};

// ── Section wrappers ─────────────────────────────────────────────────────
export const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <h3 className="text-base font-bold leading-6 text-gray-900">{children}</h3>
);

export const Divider: React.FC = () => <div className="h-px w-full bg-[#F1F1F3]" role="separator" />;

/** White rounded card (saved item card + inline "add" form card) */
export const Card: React.FC<{
    children: React.ReactNode;
    className?: string;
}> = ({ children, className = "" }) => (
    <div className={`w-full rounded-3xl border border-gray-200 bg-white p-4 ${className}`}>{children}</div>
);

// ── Inputs ───────────────────────────────────────────────────────────────
type TextFieldProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange"> & {
    label?: React.ReactNode;
    required?: boolean;
    error?: string;
    hint?: React.ReactNode;
    onValueChange?: (value: string) => void;
};

export const TextField: React.FC<TextFieldProps> = ({
    label,
    required,
    error,
    hint,
    onValueChange,
    className = "",
    id,
    ...rest
}) => {
    const fieldId = id ?? rest.name;

    return (
        <div className="flex w-full flex-col gap-1">
            {label && (
                <FieldLabel required={required} htmlFor={fieldId}>
                    {label}
                </FieldLabel>
            )}
            <input
                {...rest}
                id={fieldId}
                onChange={(event) => onValueChange?.(event.target.value)}
                className={`${INPUT_BASE} h-9 ${error ? "!border-red-400" : ""} ${className}`}
            />
            {hint && <p className="text-xs text-gray-500">{hint}</p>}
            <FieldError message={error} />
        </div>
    );
};

type TextAreaFieldProps = Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "onChange"> & {
    label?: React.ReactNode;
    required?: boolean;
    error?: string;
    counterText?: string;
    onValueChange?: (value: string) => void;
};

export const TextAreaField: React.FC<TextAreaFieldProps> = ({
    label,
    required,
    error,
    counterText,
    onValueChange,
    id,
    ...rest
}) => {
    const fieldId = id ?? rest.name;

    return (
        <div className="flex w-full flex-col gap-1">
            {label && (
                <FieldLabel required={required} htmlFor={fieldId}>
                    {label}
                </FieldLabel>
            )}
            <textarea
                rows={3}
                {...rest}
                id={fieldId}
                onChange={(event) => onValueChange?.(event.target.value)}
                className={`${INPUT_BASE} min-h-[76px] resize-y py-2 leading-5 ${error ? "!border-red-400" : ""}`}
            />
            {counterText && <p className="text-xs text-gray-500">{counterText}</p>}
            <FieldError message={error} />
        </div>
    );
};

export type Option = { value: string; label: string };

type SelectFieldProps = {
    label?: React.ReactNode;
    required?: boolean;
    error?: string;
    name?: string;
    value: string;
    placeholder: string;
    options: Option[];
    disabled?: boolean;
    onValueChange: (value: string) => void;
    className?: string;
};

/** Native select with Figma chevron – reliable on mobile, keyboard accessible */
export const SelectField: React.FC<SelectFieldProps> = ({
    label,
    required,
    error,
    name,
    value,
    placeholder,
    options,
    disabled,
    onValueChange,
    className = "",
}) => (
    <div className={`flex w-full flex-col gap-1 ${className}`}>
        {label && (
            <FieldLabel required={required} htmlFor={name}>
                {label}
            </FieldLabel>
        )}
        <div className="relative">
            <select
                id={name}
                name={name}
                value={value}
                disabled={disabled}
                onChange={(event) => onValueChange(event.target.value)}
                className={`${INPUT_BASE} h-9 appearance-none pr-9 ${value ? "text-gray-900" : "text-gray-500"} ${
                    error ? "!border-red-400" : ""
                }`}
            >
                <option value="" disabled>
                    {placeholder}
                </option>
                {options.map((option) => (
                    <option key={option.value} value={option.value} className="text-gray-900">
                        {option.label}
                    </option>
                ))}
            </select>
            <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-900" />
        </div>
        <FieldError message={error} />
    </div>
);

// ── Buttons ──────────────────────────────────────────────────────────────
type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
    loading?: boolean;
};

export const PrimaryButton: React.FC<ButtonProps> = ({ children, loading, className = "", disabled, ...rest }) => (
    <button
        type="button"
        {...rest}
        disabled={disabled || loading}
        className={`inline-flex h-8 min-w-8 items-center justify-center rounded-full bg-[#0485F7] px-3 text-sm font-medium text-white transition-colors hover:bg-[#0374d8] disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    >
        {children}
    </button>
);

export const GhostButton: React.FC<ButtonProps> = ({ children, className = "", ...rest }) => (
    <button
        type="button"
        {...rest}
        className={`inline-flex h-8 min-w-8 items-center justify-center gap-1 rounded-full bg-[#EBEBEC] px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    >
        {children}
    </button>
);

/** Grey pill with blue label + plus icon ("Add social media link" …) */
export const AddPill: React.FC<ButtonProps> = ({ children, className = "", ...rest }) => (
    <button
        type="button"
        {...rest}
        className={`inline-flex h-8 w-fit items-center gap-1.5 rounded-full bg-[#EBEBEC] px-3 text-sm font-medium text-[#1553B6] transition-colors hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    >
        <PlusIcon className="h-4 w-4" strokeWidth={2} />
        {children}
    </button>
);

/** "Save change" – light blue / disabled until the section is dirty */
export const SaveButton: React.FC<{
    dirty: boolean;
    saving: boolean;
    label: string;
    savingLabel: string;
    onClick: () => void;
}> = ({ dirty, saving, label, savingLabel, onClick }) => (
    <PrimaryButton onClick={onClick} disabled={!dirty || saving} className="w-fit">
        {saving ? savingLabel : label}
    </PrimaryButton>
);

/** Cancel + confirm footer used by every inline add/edit card */
export const FormActions: React.FC<{
    cancelLabel: string;
    confirmLabel: string;
    busy?: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}> = ({ cancelLabel, confirmLabel, busy, onCancel, onConfirm }) => (
    <div className="flex items-center justify-end gap-2 pt-1">
        <GhostButton onClick={onCancel} disabled={busy}>
            {cancelLabel}
        </GhostButton>
        <PrimaryButton onClick={onConfirm} loading={busy}>
            {confirmLabel}
        </PrimaryButton>
    </div>
);
